import { useEffect, useState, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { api, mediaUrl } from '@/admin/api';

const CACHE_KEY = 'hero_slides_v1';

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {}
}

export default function HeroCarousel({ className = '', autoplayMs = 5000 }) {
  // Hydrate from cache for instant repeat visits — no "demo" placeholder ever
  const [slides, setSlides] = useState(() => readCache());
  const [fetched, setFetched] = useState(false);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [broken, setBroken] = useState({}); // slide.id -> true when image errored

  // Fetch real slides from API (single retry on failure)
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = () =>
      api
        .get('/api/hero-slides')
        .then((r) => {
          if (cancelled) return;
          const data = Array.isArray(r.data) ? r.data : [];
          if (data.length > 0) {
            setSlides(data);
            writeCache(data);
          }
          setFetched(true);
        });
    fetchOnce().catch(() => {
      // one silent retry after 1.5s
      setTimeout(() => {
        if (!cancelled) fetchOnce().catch(() => setFetched(true));
      }, 1500);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Filter out slides whose image failed to load
  const visible = useMemo(
    () => slides.filter((s) => !broken[s.id]),
    [slides, broken]
  );

  // Keep idx within bounds when visible list changes
  useEffect(() => {
    if (idx >= visible.length && visible.length > 0) setIdx(0);
  }, [visible.length, idx]);

  const next = useCallback(
    () => setIdx((i) => (visible.length ? (i + 1) % visible.length : 0)),
    [visible.length]
  );
  const prev = useCallback(
    () => setIdx((i) => (visible.length ? (i - 1 + visible.length) % visible.length : 0)),
    [visible.length]
  );

  useEffect(() => {
    if (paused || visible.length <= 1) return;
    const t = setInterval(next, autoplayMs);
    return () => clearInterval(t);
  }, [paused, next, autoplayMs, visible.length]);

  const onKey = (e) => {
    if (e.key === 'ArrowRight') next();
    if (e.key === 'ArrowLeft') prev();
  };

  const hasSlides = visible.length > 0;
  const loading = !fetched && visible.length === 0;

  return (
    <div
      className={`relative group overflow-hidden rounded-2xl shadow-elevated bg-gradient-to-br from-primary/10 to-accent/10 ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onKeyDown={onKey}
      tabIndex={0}
      data-testid="hero-carousel"
      aria-roledescription="carousel"
    >
      <div className="relative w-full h-[500px]">
        {/* Loading state */}
        {loading && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            data-testid="hero-loading"
          >
            <Loader2 className="w-8 h-8 animate-spin text-primary/60" />
          </div>
        )}

        {/* Empty / all-broken state */}
        {!loading && !hasSlides && (
          <div
            className="absolute inset-0 flex items-center justify-center flex-col text-center p-8"
            data-testid="hero-empty"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
              <span className="text-white font-bold text-2xl font-serif">MT</span>
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              Hero images will appear here. Add slides from Admin → Hero Slides.
            </p>
          </div>
        )}

        {/* Actual slides */}
        {visible.map((s, i) => (
          <div
            key={s.id || i}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              i === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            aria-hidden={i !== idx}
            data-testid={`hero-slide-${i}`}
          >
            <img
              src={mediaUrl(s.image_url)}
              alt={s.title || `Slide ${i + 1}`}
              className="w-full h-full object-cover"
              loading={i === 0 ? 'eager' : 'lazy'}
              onError={() => setBroken((b) => ({ ...b, [s.id]: true }))}
            />
            {(s.title || s.subtitle) && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6 pt-16 text-white">
                {s.title && (
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold drop-shadow">
                    {s.title}
                  </h3>
                )}
                {s.subtitle && (
                  <p className="text-sm sm:text-base mt-1 text-white/90 max-w-md drop-shadow">
                    {s.subtitle}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Arrows */}
      {visible.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            data-testid="hero-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/70 hover:bg-white text-foreground backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-medium"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            data-testid="hero-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/70 hover:bg-white text-foreground backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-medium"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Dots */}
      {visible.length > 1 && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2"
          role="tablist"
        >
          {visible.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              role="tab"
              aria-selected={i === idx}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              className={`h-2 rounded-full transition-all ${
                i === idx ? 'w-6 bg-white' : 'w-2 bg-white/60 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
